import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { settingsApi } from '@/api/endpoints'
import { errorMessage } from '@/api/client'
import { Alert, ErrorNote, Loading, PageHeader } from '@/components/common/Ui'
import { PERMS, can, useAuth } from '@/store/auth'
import AiProviders from '@/pages/AiProviders'
import Locations from '@/pages/Locations'
import LogoSettings from '@/pages/LogoSettings'
import EmailTest from '@/pages/EmailTest'

/**
 * The settings an operator controls, as the server describes them.
 *
 * The field list comes from the API rather than being written out here, so
 * there is one definition of what is editable and what it means. Adding a
 * setting is a backend change alone.
 *
 * A secret is write-only: the server sends `value: null` and a four-character
 * hint, never the key. Leaving the box empty keeps the stored key — only
 * typing something replaces it.
 *
 * Sections come from the server too: each field carries a `group`, and the
 * tabs are built from the order the API lists. Adding the WhatsApp screen
 * was a backend change; a future section will be as well.
 */
export default function Settings() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  // Matches the API's gate on PUT /settings: a permission, not a role,
  // so a super admin widening it in the Roles screen takes effect here.
  const canEdit = can(user, PERMS.settingsWrite)

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['settings'],
    queryFn: settingsApi.get,
  })

  // The open section lives in the URL, so a WhatsApp setup can be linked to
  // directly rather than described as 'settings, then the second tab'.
  const [params, setParams] = useSearchParams()

  // Only what the operator actually touched is sent, so a field someone else
  // changed while this page was open is not overwritten with a stale value.
  const [draft, setDraft] = useState({})
  const [error, setError] = useState('')
  // Secrets whose input is open. A set key shows as ●●●● until someone asks
  // to replace it, so the page says 'this is configured' rather than showing
  // a blank box that looks like it is not.
  const [replacing, setReplacing] = useState(() => new Set())
  const [saved, setSaved] = useState(false)
  // Held so a second save restarts the countdown rather than letting the
  // first one hide the new message early.
  const successTimer = useRef(null)
  // The save bar is at the end of a long page and the alert is at the top,
  // so the result of a save would otherwise appear somewhere the operator is
  // not looking. Scrolling to an anchor rather than calling window.scrollTo
  // keeps this working if the content area ever becomes the scroller.
  const topRef = useRef(null)

  useEffect(() => () => clearTimeout(successTimer.current), [])

  const showAlert = () => {
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    topRef.current?.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' })
  }

  useEffect(() => {
    setDraft({})
  }, [data])

  // Asked, not assumed. This page looked identical whether a key was valid
  // or expired, and the first sign of a dead one was a customer asking where
  // their order went.
  const check = useMutation({
    mutationFn: settingsApi.check,
  })

  const save = useMutation({
    mutationFn: (updates) => settingsApi.update(updates),
    onSuccess: (fresh) => {
      queryClient.setQueryData(['settings'], fresh)
      setDraft({})
      setError('')
      setSaved(true)
      clearTimeout(successTimer.current)
      successTimer.current = setTimeout(() => setSaved(false), 6000)
      showAlert()
    },
    onError: (err) => {
      setError(errorMessage(err))
      setSaved(false)
      showAlert()
    },
  })

  if (isLoading) return <Loading />
  if (isError) return <ErrorNote message="Could not load settings" onRetry={refetch} />

  const changed = Object.keys(draft)
  const valueOf = (field) =>
    field.key in draft ? draft[field.key] : field.value

  const set = (key, value) =>
    setDraft((d) => {
      const next = { ...d, [key]: value }
      // Picking a service drops a model that service does not serve, so the
      // draft matches what the dropdown will show rather than leaving the
      // old name to be submitted.
      for (const field of data.fields) {
        if (field.models_of !== key || !field.models_kind) continue
        const models = data.provider_models?.[value]?.[field.models_kind] ?? []
        const chosen = field.key in next ? next[field.key] : field.value
        const claimedElsewhere = Object.entries(data.provider_models ?? {}).some(
          ([code, jobs]) =>
            code !== value && (jobs?.[field.models_kind] ?? []).includes(chosen),
        )
        if (models.length && !models.includes(chosen) && claimedElsewhere) {
          next[field.key] = models[0]
        }
      }
      return next
    })

  // The server computes each list from what is saved. While a change is
  // still a draft, the models of the service *being picked* are the ones to
  // show — otherwise choosing OpenAI leaves OpenRouter's models on screen
  // until the page is saved and reloaded.
  const optionsFor = (field) => {
    const fallback = field.options?.length
      ? field.options
      : (field.choices ?? []).map((c) => ({ value: c, label: c }))

    if (!field.models_of || !(field.models_of in draft)) return fallback

    const service = draft[field.models_of]
    const models = data.provider_models?.[service]?.[field.models_kind] ?? []
    if (!models.length) return fallback

    const chosen = field.key in draft ? draft[field.key] : field.value
    const out = models.map((m) => ({ value: m, label: m }))

    // The value in force is kept visible only when it could belong to the
    // service being picked. A model named by another service is a leftover:
    // showing 'google/gemini-2.5-flash (in use)' under OpenAI offers a model
    // OpenAI does not serve, and saving it would send them a name they
    // refuse.
    const claimedElsewhere = Object.entries(data.provider_models ?? {}).some(
      ([code, jobs]) =>
        code !== service && (jobs?.[field.models_kind] ?? []).includes(chosen),
    )
    if (chosen && !models.includes(chosen) && !claimedElsewhere) {
      out.unshift({ value: chosen, label: `${chosen} (in use)` })
    }
    return out
  }

  // Fall back to the first group for an unknown or absent ?section.
  // Rendered by its own page rather than as settings rows: a provider is a
  // service with a key and a connection test, not a field with a value.
  const PROVIDERS_TAB = 'AI providers'
  // Also its own screen: states and cities are lists to manage, not fields.
  const LOCATIONS_TAB = 'States & cities'
  const groups = [
    PROVIDERS_TAB,
    ...(data.groups?.length ? data.groups : ['General']),
    LOCATIONS_TAB,
  ]
  // Tabs drawn by their own component, with no settings fields or Save bar.
  const ownScreen = (name) => name === PROVIDERS_TAB || name === LOCATIONS_TAB
  const slug = (name) => name.toLowerCase().replace(/\s+/g, '-')
  const requested = params.get('section')
  const section = groups.find((g) => slug(g) === requested) ?? groups[0]
  // A field belonging to a service nobody has selected is hidden, not
  // dimmed: Sarvam's speech model has no meaning on a step reading with
  // Whisper, and leaving it there still asks somebody to read and dismiss
  // it. Read from the draft so switching takes effect before a save.
  const valueOfSetting = (key) =>
    key in draft ? draft[key] : data.fields.find((f) => f.key === key)?.value

  const appliesNow = (field) => {
    if (field.hidden_when) {
      const [key, values] = field.hidden_when
      if (values.includes(String(valueOfSetting(key) ?? ''))) return false
    }
    if (!field.shown_when) return true
    const [key, wanted] = field.shown_when
    return String(valueOfSetting(key) ?? '') === wanted
  }

  const shown = data.fields
    .filter((f) => (f.group ?? 'General') === section)
    .filter(appliesNow)

  // A setting whose provider is not selected still applies the moment someone
  // switches to it, so it is shown rather than hidden — but dimmed and
  // labelled, because a shop owner reading a page of twelve controls should
  // be able to see at a glance which four are doing the work today.
  const valueNow = (key) => {
    const field = data.fields.find((f) => f.key === key)
    if (!field) return undefined
    return key in draft ? draft[key] : field.value
  }
  // A step names a service now; 'rules' and 'stub' are the local options.
  const usingLlm = !['rules', 'stub', ''].includes(valueNow('EXTRACTION_PROVIDER'))
  const usingVision = !['sarvam', 'stub', ''].includes(valueNow('OCR_PROVIDER'))
  const usingSarvamStt = valueNow('STT_PROVIDER') === 'sarvam'
  // The reading style is a saaras:v3 parameter. v4 does not accept it, and
  // faster_whisper has never heard of it.
  const modeApplies = usingSarvamStt && valueNow('SARVAM_STT_MODEL') === 'saaras:v3'
  const usingSarvamOcr = valueNow('OCR_PROVIDER') === 'sarvam'

  const idleReason = (key) => {
    switch (key) {
      case 'SARVAM_STT_MODEL':
        return usingSarvamStt ? null : 'Not in use — voice notes are read on this server.'
      case 'SARVAM_STT_MODE':
        if (!usingSarvamStt) return 'Not in use — voice notes are read on this server.'
        return modeApplies ? null : 'Not in use — only saaras:v3 accepts a reading style.'
      case 'SARVAM_API_KEY':
        return usingSarvamStt || usingSarvamOcr
          ? null
          : 'Not in use — nothing is being sent to Sarvam.'
      case 'SARVAM_OCR_LANGUAGE':
        return usingSarvamOcr ? null : 'Not in use — photographs are read elsewhere.'
      case 'LLM_EXTRACTION_MODEL':
        return usingLlm ? null : 'Not in use — orders are read by the rules.'
      case 'VISION_OCR_MODEL':
        return usingVision ? null : 'Not in use — photographs are read by Sarvam.'
      case 'LLM_BASE_URL':
      case 'LLM_API_KEY':
        return usingLlm || usingVision
          ? null
          : 'Not in use — no model is reading anything yet.'
      default:
        return null
    }
  }

  // Stage headings, in the order the server lists them, limited to the ones
  // that have a setting on this tab.
  // Each tab tests the services whose credentials it holds: the keys live
  // on 'AI providers', the WhatsApp token on its own tab, and 'Reading
  // orders' only chooses between what is already connected.
  // Only WhatsApp still tests from this page; the AI services are tested
  // one at a time on their own tab, beside the key each one belongs to.
  const servicesOnThisTab = section === 'WhatsApp' ? ['whatsapp'] : []

  const stages = (data.stages ?? []).filter((st) => shown.some((f) => f.stage === st))
  const ungrouped = shown.filter((f) => !f.stage)
  const pendingIn = (group) =>
    data.fields.filter((f) => f.group === group && f.key in draft).length

  return (
    <>
      {/* Scroll target for the alert above the header. */}
      <div ref={topRef} />

      <PageHeader
        title="Settings"
        subtitle="Your company details, and how orders are read and matched to the catalog. Changes take effect on the next message."
      />

      <Alert
        tone="success"
        message={saved ? 'Saved.' : ''}
        onClose={() => {
          clearTimeout(successTimer.current)
          setSaved(false)
        }}
      />
      <Alert tone="danger" message={error} onClose={() => setError('')} />

      {groups.length > 1 && (
        <nav className="tabs" aria-label="Settings sections">
          {groups.map((group) => {
            const pending = pendingIn(group)
            return (
              <button
                key={group}
                type="button"
                className={`tab${group === section ? ' active' : ''}`}
                onClick={() => setParams(group === groups[0] ? {} : { section: slug(group) })}
              >
                {group}
                {pending > 0 && <span className="tab-dot" title={`${pending} unsaved`} />}
              </button>
            )
          })}
        </nav>
      )}

      {/* Each tab reports on its own services. Sarvam reads every voice note
          and photograph, and a revoked key looks exactly like a working one
          here — orders simply stop arriving. */}
      {servicesOnThisTab.length > 0 && (
        <section className="panel service-check">
          <div className="service-check-head">
            <div>
              <strong>Test connection</strong>
              <p className="hint">
                {section === 'WhatsApp'
                  ? 'Asks Meta whether this token and number still work. Nothing is sent to a customer.'
                  : 'Asks each service whether its key still works. Nothing is sent to a customer.'}
              </p>
            </div>
            {canEdit && (
              <button
                className="btn"
                disabled={check.isPending}
                onClick={() => check.mutate()}
              >
                {check.isPending ? 'Testing…' : 'Test connection'}
              </button>
            )}
          </div>

          {check.isError && (
            <Alert
              tone="danger"
              message={errorMessage(check.error)}
              onClose={() => check.reset()}
            />
          )}

          {check.data && (
            <ul className="service-list">
              {check.data
                .filter((svc) => servicesOnThisTab.includes(svc.service))
                .map((svc) => (
                  <li
                    key={svc.service}
                    className={svc.ok ? (svc.in_use ? 'svc-ok' : 'svc-idle') : 'svc-bad'}
                  >
                    <span className="svc-mark">
                      {svc.ok ? (svc.in_use ? '✓' : '–') : '✕'}
                    </span>
                    <span className="svc-label">{svc.label}</span>
                    <span className="svc-detail">{svc.detail}</span>
                  </li>
                ))}
            </ul>
          )}
        </section>
      )}

      {section === PROVIDERS_TAB && <AiProviders />}
      {section === LOCATIONS_TAB && <Locations />}

      {!ownScreen(section) && !canEdit && (
        <p className="hint">
          These are read-only for your account — an admin can change them.
        </p>
      )}

      <div className="settings-list" hidden={ownScreen(section)}>
        {section === 'Company' && <LogoSettings canEdit={canEdit} />}
        {stages.map((stage) => (
          <div className="settings-stage" key={stage}>
            <h2 className="stage-heading">{stage}</h2>
            {shown.filter((f) => f.stage === stage).map((field) => renderField(field))}
          </div>
        ))}
        {ungrouped.map((field) => renderField(field))}
        {section === 'Email' && (
          <EmailTest
            canEdit={canEdit}
            unsaved={changed.some((key) => data.fields.find((f) => f.key === key)?.group === 'Email')}
          />
        )}
      </div>

      {canEdit && !ownScreen(section) && (
        <div className="settings-actions">
          <button
            className="btn btn-primary"
            disabled={changed.length === 0 || save.isPending}
            onClick={() => {
              setError('')
              // A blank box means 'leave it' everywhere but a text field,
              // where blank is how a company detail is cleared.
              const clearable = (key) =>
                data.fields.find((f) => f.key === key)?.kind === 'text'
              save.mutate(
                changed
                  .filter((key) => draft[key] !== '' || clearable(key))
                  .map((key) => ({ key, value: draft[key] })),
              )
            }}
          >
            {save.isPending
              ? 'Saving…'
              : changed.length > 1
                ? `Save ${changed.length} changes`
                : 'Save'}
          </button>
          {changed.length > 0 && (
            <button className="btn" onClick={() => setDraft({})}>
              Discard
            </button>
          )}
          {changed.some((key) => {
            const f = data.fields.find((x) => x.key === key)
            return f && (f.group ?? 'General') !== section
          }) && <span className="hint">Includes changes in another section.</span>}
        </div>
      )}
    </>
  )

  function renderField(field) {
    const idle = idleReason(field.key)
    return (
          <section
            className={`panel settings-row${idle ? ' settings-idle' : ''}`}
            key={field.key}
          >
            <div className="settings-label">
              <label htmlFor={field.key}>{field.label}</label>
              {field.overridden && (
                <span className="tag" title="Changed here, overriding the server's .env file">
                  changed
                </span>
              )}
              {idle && <span className="tag tag-idle">not in use</span>}
            </div>

            {idle && <p className="hint idle-note">{idle}</p>}
            <p className="hint">{field.help}</p>
            {field.caution && <p className="hint caution">⚠ {field.caution}</p>}

            <div className="settings-control">
              {field.kind === 'bool' && (
                <label className="switch">
                  <input
                    id={field.key}
                    type="checkbox"
                    disabled={!canEdit}
                    checked={Boolean(valueOf(field))}
                    onChange={(e) => set(field.key, e.target.checked)}
                  />
                  <span>{valueOf(field) ? 'On' : 'Off'}</span>
                </label>
              )}

              {field.kind === 'choice' && optionsFor(field).length > 0 && (
                <select
                  id={field.key}
                  disabled={!canEdit}
                  value={valueOf(field) ?? ''}
                  onChange={(e) => set(field.key, e.target.value)}
                >
                  {/* Named services, not codes: an operator who connects
                      OpenRouter should see OpenRouter here, not 'vision'.
                      A model list follows the service picked above it
                      immediately — waiting for a save and a reload would
                      leave one service's models listed while another was
                      selected. */}
                  {optionsFor(field).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              )}

              {/* Free text only when nothing can be offered — a service we
                  have listed no models for. Deciding this on the server's
                  list left the field an input after a service with models
                  had been picked, because the list is computed from what is
                  saved rather than from the draft. */}
              {field.kind === 'choice' && optionsFor(field).length === 0 && (
                <input
                  id={field.key}
                  type="text"
                  disabled={!canEdit}
                  value={valueOf(field) ?? ''}
                  onChange={(e) => set(field.key, e.target.value)}
                />
              )}

              {field.kind === 'text' && field.multiline && (
                <textarea
                  id={field.key}
                  rows={4}
                  disabled={!canEdit}
                  placeholder={field.placeholder ?? undefined}
                  value={valueOf(field) ?? ''}
                  onChange={(e) => set(field.key, e.target.value)}
                />
              )}

              {field.kind === 'text' && !field.multiline && (
                <input
                  id={field.key}
                  type="text"
                  disabled={!canEdit}
                  placeholder={field.placeholder ?? undefined}
                  value={valueOf(field) ?? ''}
                  onChange={(e) => set(field.key, e.target.value)}
                />
              )}

              {field.kind === 'number' && (
                <input
                  id={field.key}
                  type="number"
                  step="0.01"
                  min={field.minimum ?? undefined}
                  max={field.maximum ?? undefined}
                  disabled={!canEdit}
                  value={valueOf(field) ?? ''}
                  onChange={(e) => set(field.key, e.target.value)}
                />
              )}

              {field.kind === 'secret' && (
                <div className="secret-field">
                  {/* A set key shows that it is set and which one it is,
                      rather than an empty box under three lines explaining
                      why it is empty. The input appears when someone says
                      they want to replace it. */}
                  {field.is_set && !replacing.has(field.key) ? (
                    <div className="secret-set">
                      <span className="secret-mask">
                        ●●●● {field.hint ? field.hint.replace('…', '') : ''}
                      </span>
                      {canEdit && (
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={() =>
                            setReplacing((r) => new Set(r).add(field.key))
                          }
                        >
                          Replace
                        </button>
                      )}
                    </div>
                  ) : (
                    <>
                      <input
                        id={field.key}
                        type="password"
                        autoComplete="new-password"
                        disabled={!canEdit}
                        placeholder={field.is_set ? 'Paste the new value' : 'Not set'}
                        value={draft[field.key] ?? ''}
                        onChange={(e) => set(field.key, e.target.value)}
                      />
                      {field.is_set && (
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={() => {
                            setReplacing((r) => {
                              const next = new Set(r)
                              next.delete(field.key)
                              return next
                            })
                            setDraft((d) => {
                              const next = { ...d }
                              delete next[field.key]
                              return next
                            })
                          }}
                        >
                          Keep the current one
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </section>
    )
  }
}
